/**
 * SectorsPage — Gestion des secteurs d'activité
 * CRUD complet via REST /api/sectors
 */

import { useState, useEffect, useCallback } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CategoryIcon from '@mui/icons-material/Category';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.e-kaa.app';
const TOKEN_KEY = 'ekaa_admin_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface BusinessSector {
  id: string;
  label: string;
  emoji: string | null;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

type SectorDraft = Partial<Pick<BusinessSector, 'label' | 'emoji' | 'isActive' | 'displayOrder'>>;

export function SectorsPage() {
  const notify = useNotify();

  const [sectors, setSectors] = useState<BusinessSector[]>([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, SectorDraft>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});

  // New sector form
  const [newLabel, setNewLabel] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [newOrder, setNewOrder] = useState('');
  const [creating, setCreating] = useState(false);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<BusinessSector | null>(null);

  const fetchSectors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sectors/all`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) setSectors(data);
    } catch (err: any) {
      notify(`Erreur de chargement: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  // ── Field helpers ──────────────────────────────────────────────────────────

  function getField<K extends keyof BusinessSector>(sector: BusinessSector, key: K): BusinessSector[K] {
    return (edits[sector.id]?.[key as keyof SectorDraft] as BusinessSector[K]) ?? sector[key];
  }

  function setField(id: string, field: keyof SectorDraft, value: unknown) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  function isDirty(id: string) {
    return !!edits[id] && Object.keys(edits[id]).length > 0;
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave(sector: BusinessSector) {
    if (!edits[sector.id]) return;
    setSaving((p) => ({ ...p, [sector.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/sectors/${sector.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(edits[sector.id]),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.message || `HTTP ${res.status}`);
      setSectors((prev) => prev.map((s) => (s.id === sector.id ? updated : s)));
      setEdits((prev) => { const c = { ...prev }; delete c[sector.id]; return c; });
      notify('Secteur mis à jour', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setSaving((p) => ({ ...p, [sector.id]: false }));
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleDelete(sector: BusinessSector) {
    setDeleteTarget(null);
    setDeleting((p) => ({ ...p, [sector.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/sectors/${sector.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setSectors((prev) => prev.filter((s) => s.id !== sector.id));
      notify('Secteur supprimé', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setDeleting((p) => ({ ...p, [sector.id]: false }));
    }
  }

  // ── Create ────────────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/sectors`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          label: newLabel.trim(),
          emoji: newEmoji.trim() || undefined,
          displayOrder: newOrder ? parseInt(newOrder, 10) : 0,
        }),
      });
      const created = await res.json();
      if (!res.ok) throw new Error(created.message || `HTTP ${res.status}`);
      setSectors((prev) =>
        [...prev, created].sort((a, b) => a.displayOrder - b.displayOrder)
      );
      setNewLabel('');
      setNewEmoji('');
      setNewOrder('');
      notify('Secteur créé', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setCreating(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Secteurs d'activité" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CategoryIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Secteurs d'activité
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {sectors.length} secteur{sectors.length !== 1 ? 's' : ''} · Affichés dans le formulaire de création de commerce
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={fetchSectors} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Les modifications sont immédiates sur le formulaire de création de commerce et la page de recherche.
      </Alert>

      {/* Add form */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Ajouter un secteur
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Emoji"
            value={newEmoji}
            onChange={(e) => setNewEmoji(e.target.value)}
            sx={{ width: 90 }}
            inputProps={{ style: { textAlign: 'center', fontSize: 20 } }}
            placeholder="🏷️"
          />
          <TextField
            size="small"
            label="Label *"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ flex: 1, minWidth: 200 }}
            placeholder="Ex: Coiffure & beauté"
          />
          <TextField
            size="small"
            label="Ordre"
            type="number"
            value={newOrder}
            onChange={(e) => setNewOrder(e.target.value)}
            sx={{ width: 90 }}
            inputProps={{ min: 0 }}
          />
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            onClick={handleCreate}
            disabled={!newLabel.trim() || creating}
          >
            Ajouter
          </Button>
        </Box>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Emoji</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Ordre</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Actif</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sectors.map((sector) => {
                const dirty = isDirty(sector.id);
                return (
                  <TableRow
                    key={sector.id}
                    sx={{
                      bgcolor: dirty ? 'warning.50' : undefined,
                      '&:hover': { bgcolor: dirty ? 'warning.100' : 'action.hover' },
                    }}
                  >
                    {/* Emoji */}
                    <TableCell sx={{ width: 90 }}>
                      <TextField
                        size="small"
                        value={(getField(sector, 'emoji') as string | null) ?? ''}
                        onChange={(e) => setField(sector.id, 'emoji', e.target.value || null)}
                        sx={{ width: 70 }}
                        inputProps={{ style: { textAlign: 'center', fontSize: 20 }, title: 'Emoji (optionnel)' }}
                      />
                    </TableCell>

                    {/* Label */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          value={getField(sector, 'label') as string}
                          onChange={(e) => setField(sector.id, 'label', e.target.value)}
                          sx={{ minWidth: 220 }}
                        />
                        {dirty && (
                          <Chip label="modifié" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>

                    {/* Order */}
                    <TableCell sx={{ textAlign: 'center', width: 100 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={getField(sector, 'displayOrder') as number}
                        onChange={(e) => setField(sector.id, 'displayOrder', parseInt(e.target.value, 10) || 0)}
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 60 } }}
                        sx={{ width: 80 }}
                      />
                    </TableCell>

                    {/* Active toggle */}
                    <TableCell sx={{ textAlign: 'center', width: 80 }}>
                      <Switch
                        checked={getField(sector, 'isActive') as boolean}
                        onChange={(e) => setField(sector.id, 'isActive', e.target.checked)}
                        color="success"
                        size="small"
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ textAlign: 'right', width: 140 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Enregistrer">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSave(sector)}
                              disabled={!dirty || saving[sector.id]}
                            >
                              {saving[sector.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <SaveIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(sector)}
                              disabled={deleting[sector.id]}
                            >
                              {deleting[sector.id] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {sectors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Aucun secteur enregistré.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer le secteur</DialogTitle>
        <DialogContent>
          <Typography>
            Supprimer <strong>"{deleteTarget?.label}"</strong> ? Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteTarget && handleDelete(deleteTarget)}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SectorsPage;
